// src/Components/Home/Home.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Slider from 'react-slick';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import { useNavigate, useLocation } from 'react-router-dom';

// Icons
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CropIcon from '@mui/icons-material/Crop';
import LanguageIcon from '@mui/icons-material/Language';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { MenuResetView, Grid5x5Icon } from '../../assets/icons/CustomIconsAdd4';

import { collection, query, where, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebase/firebase';

import MediaSlide from '../Shared/MediaSlide';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import ContactDialog from '../ContactDialog/ContactDialog';

import packageInfo from '../../../package.json';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export default function Home({ selectedMarker }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showcaseSlides, setShowcaseSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // State to hold the global restricted settings
  const [restrictedSettings, setRestrictedSettings] = useState(null);

  // State to track if the current active user is permitted across the entire component
  const [isPermittedUser, setIsPermittedUser] = useState(false);

  // View States
  const [isPaused, setIsPaused] = useState(false);
  const [isCoverMode, setIsCoverMode] = useState(true);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Global Sound Toggle - Session Storage implementation
  const [isGlobalMuted, setIsGlobalMuted] = useState(() => {
    const stored = sessionStorage.getItem('loktr_global_mute');
    if (stored === null) return true; // Sound OFF by default
    return stored === 'true';
  });

  // Contact Dialog State
  const [isContactOpen, setIsContactOpen] = useState(false);

  // Layout, Dragging & Sizing States for Normal View
  const [activePanel, setActivePanel] = useState('image'); // 'image' | 'description'
  const [containerPos, setContainerPos] = useState({ x: 10, y: 150 });

  // Calculate initial size to prevent bleeding off small screens
  const [containerSize, setContainerSize] = useState(() =>
    Math.min(800, window.innerHeight - 160),
  );

  const [isDragging, setIsDragging] = useState(false);
  const dragInfo = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });

  const resizeInfo = useRef({ isResizing: false, startX: 0, startY: 0, initialSize: 0 });

  // State for URL Validation Trap
  const [showUrlError, setShowUrlError] = useState(false);

  // Refs
  const drawerSliderRef = useRef(null);
  const resumeTimerRef = useRef(null);
  const isRecoveringRef = useRef(false);

  const isAutoMode = !selectedMarker;

  // --- 0. INTERCEPT STATE FROM FULLSCREEN SYNC ---
  useEffect(() => {
    if (location.state && location.state.syncedIndex !== undefined) {
      isRecoveringRef.current = true;
      const sIndex = location.state.syncedIndex;
      const sPaused = location.state.syncedPaused;

      setCurrentIndex(sIndex);
      setIsPaused(sPaused || false);

      if (drawerSliderRef.current) {
        setTimeout(() => {
          if (drawerSliderRef.current) {
            drawerSliderRef.current.slickGoTo(sIndex, true);
          }
          isRecoveringRef.current = false;
        }, 50);
      } else {
        isRecoveringRef.current = false;
      }

      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // --- 1. FETCH SHOWCASE DATA ---
  useEffect(() => {
    let isMounted = true;

    const checkPermittedUser = async (user) => {
      if (user && user.email) {
        try {
          const emailKey = user.email.toLowerCase().trim();
          const permRef = doc(db, 'permittedUsers', emailKey);
          const permSnap = await getDoc(permRef);
          if (isMounted) setIsPermittedUser(permSnap.exists());
        } catch (err) {
          console.warn('Could not check permissions', err);
          if (isMounted) setIsPermittedUser(false);
        }
      } else {
        if (isMounted) setIsPermittedUser(false);
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (isMounted) checkPermittedUser(user);
    });

    return () => {
      isMounted = false;
      unsubscribeAuth();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let unsubscribeQuery = null;

    const fetchShowcaseData = async () => {
      try {
        setIsLoading(true);

        const restRef = doc(db, 'settings', 'RestrictedMarkerSettings');
        const restSnap = await getDoc(restRef);
        const rSettings = restSnap.exists() ? restSnap.data() : null;
        if (isMounted) setRestrictedSettings(rSettings);

        const q = query(collection(db, 'markerGroups'), where('showcase', '==', true));

        unsubscribeQuery = onSnapshot(
          q,
          (querySnapshot) => {
            if (!isMounted) return;

            const today = new Date().toISOString().split('T')[0];
            const slides = [];

            querySnapshot.forEach((docSnapshot) => {
              const data = docSnapshot.data();
              const itemName = data.title || data.name || docSnapshot.id;
              const markerData = { id: docSnapshot.id, ...data };

              let visibility = data.visibility;
              if (!visibility) {
                if (data.publicFromDate && data.publicFromDate.trim() !== '') {
                  visibility = `Public From ${data.publicFromDate}`;
                } else if (data.isPrivate) {
                  visibility = 'Private';
                } else {
                  visibility = 'Public';
                }
              }

              let isVisible = true;
              let isRestricted = false;

              if (visibility === 'Private') {
                isVisible = false;
              } else if (visibility.startsWith('Public From')) {
                const datePart = visibility.replace('Public From', '').trim();
                if (datePart && datePart > today) {
                  isVisible = false;
                }
              } else if (visibility === 'Restricted') {
                isRestricted = true;
              }

              if (!isVisible && !isPermittedUser) {
                return;
              }

              if (isRestricted && rSettings && !isPermittedUser) {
                slides.push({
                  id: `${docSnapshot.id}-restricted`,
                  url: rSettings.RestrictedImage || '',
                  description: rSettings.RestrictedDescription || '',
                  markerName: itemName,
                  type: rSettings.RestrictedImageIsVideo ? 'video' : 'image',
                  website: data.website || '',
                  allPhotos: [
                    {
                      url: rSettings.RestrictedImage || '',
                      img: rSettings.RestrictedImage || '',
                      description: rSettings.RestrictedDescription || '',
                      type: rSettings.RestrictedImageIsVideo ? 'video' : 'image',
                    },
                  ],
                  markerDescription: rSettings.RestrictedDescription || '',
                  markerData: markerData,
                });
              } else {
                const hasPhotos = data.photos && data.photos.length > 0;
                const hasDesc =
                  data.description &&
                  data.description.trim() !== '' &&
                  data.description !== '<p></p>';
                const hasWeb = data.website && data.website.trim() !== '';

                if (hasPhotos || hasDesc || hasWeb) {
                  if (hasPhotos) {
                    const firstPhoto = data.photos[0];

                    const resolvedUrl =
                      firstPhoto.url ||
                      firstPhoto.img ||
                      firstPhoto.videoUrl ||
                      firstPhoto.video ||
                      firstPhoto.src ||
                      firstPhoto.media ||
                      firstPhoto.clipUrl ||
                      firstPhoto.downloadUrl ||
                      '';
                    let inferredType = firstPhoto.type || 'image';
                    if (
                      inferredType === 'image' &&
                      resolvedUrl.match(/\.(mp4|mov|webm|avi|mkv|wmv|flv|mts|m4v)/i)
                    )
                      inferredType = 'video';
                    if (
                      inferredType === 'image' &&
                      resolvedUrl.match(/\.(mp3|wav|ogg|aac)/i)
                    )
                      inferredType = 'audio';

                    slides.push({
                      id: firstPhoto.id || `${docSnapshot.id}-0`,
                      url: resolvedUrl,
                      description: firstPhoto.description || data.description || '',
                      markerName: itemName,
                      type: inferredType,
                      website: data.website || '',
                      allPhotos: data.photos,
                      markerDescription: data.description || '',
                      markerData: markerData,
                    });
                  } else {
                    slides.push({
                      id: `${docSnapshot.id}-no-photo`,
                      url: '',
                      description: data.description || '',
                      markerName: itemName,
                      type: 'placeholder',
                      website: data.website || '',
                      allPhotos: [],
                      markerDescription: data.description || '',
                      markerData: markerData,
                    });
                  }
                }
              }
            });

            setShowcaseSlides(slides);
            setIsLoading(false);
          },
          (error) => {
            if (error.code !== 'permission-denied') {
              console.error('Error fetching showcase projects:', error);
            }
            if (isMounted) setIsLoading(false);
          },
        );
      } catch (error) {
        if (error.code !== 'permission-denied') {
          console.error('Error fetching showcase projects:', error);
        }
        if (isMounted) setIsLoading(false);
      }
    };

    fetchShowcaseData();

    return () => {
      isMounted = false;
      if (unsubscribeQuery) unsubscribeQuery();
    };
  }, [isPermittedUser]);

  // --- 2. PREPARE ACTIVE SLIDES ---
  let activeSlides = [];
  let isRestrictedManual = false;

  if (!isAutoMode && selectedMarker) {
    let vis = selectedMarker.visibility;
    if (!vis) {
      if (selectedMarker.publicFromDate && selectedMarker.publicFromDate.trim() !== '') {
        vis = `Public From ${selectedMarker.publicFromDate}`;
      } else if (selectedMarker.isPrivate) {
        vis = 'Private';
      } else {
        vis = 'Public';
      }
    }
    if (vis === 'Restricted') {
      isRestrictedManual = true;
    }
  }

  if (isAutoMode) {
    activeSlides = showcaseSlides;
  } else {
    const manualDesc =
      isRestrictedManual && restrictedSettings && !isPermittedUser
        ? restrictedSettings.RestrictedDescription
        : selectedMarker?.description || '';
    const manualWeb = selectedMarker?.website || '';

    if (isRestrictedManual && restrictedSettings && !isPermittedUser) {
      activeSlides = [
        {
          id: 'restricted-media',
          url: restrictedSettings.RestrictedImage || '',
          description: manualDesc,
          website: manualWeb,
          type: restrictedSettings.RestrictedImageIsVideo ? 'video' : 'image',
          markerData: selectedMarker,
        },
      ];
    } else {
      if (selectedMarker?.photos && selectedMarker.photos.length > 0) {
        activeSlides = selectedMarker.photos.map((p, index) => {
          const resolvedUrl =
            p.url ||
            p.img ||
            p.videoUrl ||
            p.video ||
            p.src ||
            p.media ||
            p.clipUrl ||
            p.downloadUrl ||
            '';
          let inferredType = p.type || 'image';
          if (
            inferredType === 'image' &&
            resolvedUrl.match(/\.(mp4|mov|webm|avi|mkv|wmv|flv|mts|m4v)/i)
          )
            inferredType = 'video';
          if (inferredType === 'image' && resolvedUrl.match(/\.(mp3|wav|ogg|aac)/i))
            inferredType = 'audio';

          return {
            id: p.id,
            url: resolvedUrl,
            description: p.description || (index === 0 ? manualDesc : ''),
            website: manualWeb,
            type: inferredType,
            markerName: selectedMarker.title || selectedMarker.name || 'Details',
            markerData: selectedMarker,
          };
        });
      } else {
        activeSlides = [
          {
            id: 'no-photo',
            type: 'placeholder',
            description: manualDesc,
            website: manualWeb,
            markerData: selectedMarker,
          },
        ];
      }
    }
  }

  // --- 3. DYNAMIC RENDERING LOGIC ---
  const safeIndex =
    currentIndex >= activeSlides.length
      ? Math.max(0, activeSlides.length - 1)
      : currentIndex;
  const currentSlide = activeSlides[safeIndex];

  const currentIsTextOnly =
    currentSlide?.type === 'placeholder' ||
    currentSlide?.type === 'text' ||
    (!currentSlide?.url &&
      currentSlide?.type !== 'video' &&
      currentSlide?.type !== 'audio');

  let descriptionContent = currentSlide?.description || '';
  if (descriptionContent === '<p></p>' || descriptionContent.trim() === '') {
    descriptionContent = '';
  }

  const activeWebsite = currentSlide?.website || '';

  const hasDescData = descriptionContent !== '' || activeWebsite !== '';

  const showDescPanel = hasDescData && !currentIsTextOnly;

  const imagePanelHeight = !showDescPanel ? '100%' : '80%';
  const descPanelHeight = !showDescPanel ? '0%' : isDescriptionExpanded ? '80%' : '20%';

  useEffect(() => {
    if (isRecoveringRef.current) return;
    setCurrentIndex(0);
    if (drawerSliderRef.current) drawerSliderRef.current.slickGoTo(0, true);
  }, [selectedMarker, isAutoMode]);

  useEffect(() => {
    if (isAutoMode) {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      setTimeout(() => {
        if (drawerSliderRef.current && !isPaused) {
          drawerSliderRef.current.slickPlay();
        }
      }, 300);
    }
  }, [isAutoMode, isPaused]);

  const handleTogglePause = () => {
    const newPausedState = !isPaused;
    setIsPaused(newPausedState);
    const activeRef = drawerSliderRef.current;
    if (activeRef) {
      if (newPausedState) {
        activeRef.slickPause();
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      } else {
        activeRef.slickPlay();
      }
    }
  };

  const handleToggleFitMode = () => setIsCoverMode(!isCoverMode);

  const triggerResumeTimer = useCallback(() => {
    if (isAutoMode && !isPaused) {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = setTimeout(() => {
        const activeRef = drawerSliderRef.current;
        if (activeRef) activeRef.slickPlay();
      }, 10);
    }
  }, [isAutoMode, isPaused]);

  const handleSlideChange = useCallback(
    (current, next) => {
      setCurrentIndex(next);
      triggerResumeTimer();
    },
    [triggerResumeTimer],
  );

  const handleOpenWeb = (url) => {
    if (!url || url.trim() === '') return;
    let finalUrl = url.trim();

    if (!/^https?:\/\//i.test(finalUrl)) {
      finalUrl = 'http://' + finalUrl;
    }

    try {
      const parsedUrl = new URL(finalUrl);
      if (!parsedUrl.hostname.includes('.') && parsedUrl.hostname !== 'localhost') {
        throw new Error('Invalid Domain Structure');
      }
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.warn('Invalid URL Format caught:', finalUrl);
      setShowUrlError(true);
    }
  };

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      fade: true,
      cssEase: 'linear',
      speed: 1000,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      adaptiveHeight: false,
      autoplaySpeed: 5000,
      pauseOnHover: false,
      accessibility: false,
      focusOnSelect: false,
      lazyLoad: 'progressive',
      useTransform: true,
      touchThreshold: 10,
      useCSS: true,
      beforeChange: handleSlideChange,
    }),
    [handleSlideChange],
  );

  // --- DRAG HANDLERS ---
  const handlePointerDown = (e) => {
    if (e.target.closest('.description-panel')) {
      setActivePanel('description');
    } else if (e.target.closest('.image-panel')) {
      setActivePanel('image');
    }

    const isDragHandle = e.target.closest('.drag-handle');
    if (!isDragHandle) {
      return;
    }

    dragInfo.current.isDragging = true;
    setIsDragging(true);
    dragInfo.current.startX = e.clientX;
    dragInfo.current.startY = e.clientY;
    dragInfo.current.initialX = containerPos.x;
    dragInfo.current.initialY = containerPos.y;

    try {
      if (e.target.setPointerCapture) {
        e.target.setPointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const handlePointerMove = (e) => {
    if (!dragInfo.current.isDragging) return;
    setContainerPos({
      x: dragInfo.current.initialX + (e.clientX - dragInfo.current.startX),
      y: dragInfo.current.initialY + (e.clientY - dragInfo.current.startY),
    });
  };

  const handlePointerUp = (e) => {
    if (!dragInfo.current.isDragging) return;
    dragInfo.current.isDragging = false;
    setIsDragging(false);
    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  // --- RESIZE HANDLERS ---
  const handleResizePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = true;
    resizeInfo.current.startX = e.clientX;
    resizeInfo.current.startY = e.clientY;
    resizeInfo.current.initialSize = containerSize;
    try {
      if (e.target.setPointerCapture) {
        e.target.setPointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const handleResizePointerMove = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();

    let deltaX = e.clientX - resizeInfo.current.startX;
    let deltaY = e.clientY - resizeInfo.current.startY;

    if (currentIsTextOnly) {
      deltaX = deltaX * 1;
      deltaY = deltaY * 1;
    }

    const maxDelta = Math.max(deltaX, deltaY);
    const newSize = Math.max(400, resizeInfo.current.initialSize + maxDelta);
    setContainerSize(newSize);
  };

  const handleResizePointerUp = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = false;
    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const renderContent = () => {
    if (isAutoMode && isLoading) {
      return (
        <Box
          className='image-panel'
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
            pointerEvents: 'auto',
          }}>
          <CircularProgress />
        </Box>
      );
    }

    if (isAutoMode && activeSlides.length === 0) {
      return (
        <Box
          className='image-panel'
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#306E86',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
            pointerEvents: 'auto',
          }}>
          <Typography variant='h6' sx={{ color: '#fff', fontWeight: 'bold' }}>
            No Showcase Projects Available
          </Typography>
        </Box>
      );
    }

    // Failsafe condition
    if (activeSlides.length === 0) return null;

    const shouldAutoplay = isAutoMode && !isPaused;

    return (
      <>
        {/* IMAGE / SLIDER PANEL */}
        <Box
          className='image-panel'
          sx={{
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: imagePanelHeight,
            transition: 'height 0.3s ease',
            zIndex: activePanel === 'image' ? 2 : 1,
            backgroundColor: currentIsTextOnly ? '#fff' : '#63c2e8',
            boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
            pointerEvents: 'auto',
            overflow: 'hidden',
            '& .slick-slider': { height: '100%', width: '100%' },
            '& .slick-list': { height: '100%' },
            '& .slick-track': { height: '100%', display: 'flex', alignItems: 'center' },
            '& .slick-slide': {
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
            },
            '& .slick-slide.slick-active': { zIndex: 2 },
            '& .slick-slide > div': { height: '100%', width: '100%' },
            '& .slick-prev, & .slick-next': {
              zIndex: 10,
              width: '32px',
              height: '32px',
              display: 'flex !important',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: '1px solid #000000',
              backgroundColor: '#FFFFFF',
              opacity: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                opacity: 0.8,
              },
              '&:before': {
                content: '""',
                borderTop: '2px solid #000',
                borderLeft: '2px solid #000',
                width: '10px',
                height: '10px',
                position: 'absolute',
                opacity: 1,
              },
            },
            '& .slick-prev': {
              left: '10px',
              '&:before': {
                transform: 'rotate(-45deg)',
                marginLeft: '4px',
              },
            },
            '& .slick-next': {
              right: '10px',
              '&:before': {
                transform: 'rotate(135deg)',
                marginRight: '4px',
              },
            },
            '& .slick-dots': { bottom: '15px', zIndex: 10 },
            '& .slick-dots li button:before': { display: 'none' },
            '& .slick-dots li': { width: '14px', height: '14px', margin: '0 4px' },
            '& .slick-dots li button': {
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              padding: 0,
              border: '1px solid #000000',
              backgroundColor: '#FFFFFF',
              opacity: 0.8,
            },
            '& .slick-dots li.slick-active button': {
              border: '1px solid #FFFFFF',
              backgroundColor: '#000000',
              opacity: 1,
              transform: 'scale(1.2)',
            },
          }}>
          <IconButton
            className='drag-handle'
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 100,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              opacity: 0.5,
              cursor: 'grab',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', opacity: 1 },
            }}
            title='Drag Window'>
            <Grid5x5Icon />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 100,
              display: 'flex',
              gap: 1,
            }}>
            {!currentIsTextOnly && (
              <IconButton
                onClick={(e) => {
                  e.currentTarget.blur();
                  setIsGlobalMuted((prev) => {
                    const newVal = !prev;
                    sessionStorage.setItem('loktr_global_mute', newVal.toString());
                    return newVal;
                  });
                }}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  opacity: 0.3,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', opacity: 1 },
                }}
                title={isGlobalMuted ? 'Turn Sound On' : 'Turn Sound Off'}>
                {isGlobalMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
            )}

            <IconButton
              onClick={(e) => {
                e.currentTarget.blur();
                setContainerPos({ x: 10, y: 150 });
                setContainerSize(Math.min(800, window.innerHeight - 160));
              }}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                opacity: 0.3,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', opacity: 1 },
              }}
              title='Reset Position & Size'>
              <MenuResetView />
            </IconButton>

            {!currentIsTextOnly && (
              <IconButton
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleToggleFitMode();
                }}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  opacity: 0.3,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', opacity: 1 },
                }}
                title={isCoverMode ? 'Show Whole Media' : 'Fill Screen'}>
                {isCoverMode ? <AspectRatioIcon /> : <CropIcon />}
              </IconButton>
            )}

            {isAutoMode && (
              <IconButton
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleTogglePause();
                }}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  opacity: 0.3,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', opacity: 1 },
                }}
                title={isPaused ? 'Play Slideshow' : 'Pause Slideshow'}>
                {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
              </IconButton>
            )}

            <IconButton
              onClick={(e) => {
                e.currentTarget.blur();
                navigate('/fullscreen', {
                  state: { activeSlides, isAutoMode, currentIndex: safeIndex, isPaused },
                });
              }}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                opacity: 0.3,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)', opacity: 1 },
              }}
              title='Open Full Screen Window'>
              <OpenInFullIcon />
            </IconButton>
          </Box>

          <Box sx={{ position: 'absolute', bottom: 10, right: 20, zIndex: 100 }}>
            <Typography
              variant='caption'
              sx={{
                backgroundColor: !isAutoMode
                  ? '#FF9900'
                  : isPaused
                    ? '#f44336'
                    : '#4caf50',
                color: '#fff',
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                boxShadow: '0px 2px 5px rgba(0,0,0,0.3)',
              }}>
              {!isAutoMode ? 'Manual Mode' : isPaused ? 'Paused' : 'Auto Mode'}
            </Typography>
          </Box>

          <Slider
            {...settings}
            ref={drawerSliderRef}
            autoplay={shouldAutoplay}
            initialSlide={safeIndex}
            key={`${isAutoMode ? 'auto' : selectedMarker?.id || selectedMarker?.name || 'manual'}-${activeSlides.map((s) => s.id).join('-')}`}>
            {activeSlides.map((item, index) => {
              const isFallbackBranch =
                item.type === 'placeholder' ||
                item.type === 'text' ||
                (!item.url && item.type !== 'video' && item.type !== 'audio');

              return (
                <Box
                  key={item.id || index}
                  tabIndex={-1}
                  sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex !important',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                  }}>
                  {isFallbackBranch ? (
                    <Box
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        cursor: 'default',
                      }}>
                      {item.website && item.website.trim() !== '' && (
                        <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}>
                          <IconButton
                            onClick={() => handleOpenWeb(item.website)}
                            title='Visit Website'
                            sx={{
                              width: '48px !important',
                              height: '48px !important',
                              border: '2px solid #FF9900',
                              borderRadius: '6px',
                              color: '#00008B',
                              bgcolor: '#fff',
                              '&:hover': { bgcolor: '#f0f0f0' },
                            }}>
                            <LanguageIcon sx={{ fontSize: '32px !important' }} />
                          </IconButton>
                        </Box>
                      )}
                      <Box
                        sx={{
                          flexGrow: 1,
                          overflowY: 'auto',
                          p: 3,
                          pt: item.website && item.website.trim() !== '' ? 8 : 7,
                          pb: 8,
                          typography: 'body1',
                          color: 'text.secondary',
                          lineHeight: 1.2,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          textTransform: 'none !important',
                          '& p': { mt: 0, mb: '6pt', lineHeight: 1.2 },
                          '& p:last-of-type': { mb: 0 },
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                          },
                          '& ul, & ol': {
                            paddingLeft: '1.5rem',
                            mt: 0,
                            mb: 0,
                          },
                          '& li': { mt: 0, mb: '6pt', lineHeight: 1.1 },
                          '& li > p': { margin: '0 !important' },
                          '& a': { color: '#1976d2', textDecoration: 'none' },
                        }}>
                        {item.description ? (
                          <div dangerouslySetInnerHTML={{ __html: item.description }} />
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              width: '100%',
                              height: '100%',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            <Typography
                              variant='h6'
                              sx={{ color: '#ccc', fontStyle: 'italic' }}>
                              No Media Available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <MediaSlide
                      item={item}
                      isActive={index === safeIndex}
                      objectFit={isCoverMode ? 'cover' : 'contain'}
                      shouldAutoPlay={isAutoMode}
                      isGlobalMuted={isGlobalMuted}
                    />
                  )}
                </Box>
              );
            })}
          </Slider>
        </Box>

        {/* DESCRIPTION PANEL */}
        <Box
          className='description-panel'
          sx={{
            display: showDescPanel ? 'block' : 'none',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: descPanelHeight,
            transition: 'height 0.3s ease',
            zIndex: isDescriptionExpanded ? 3 : activePanel === 'description' ? 2 : 1,
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            p: 2,
            overflowY: isDescriptionExpanded ? 'auto' : 'hidden',
            pointerEvents: 'auto',
            boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
          }}>
          {activeWebsite && activeWebsite.trim() !== '' && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
              <IconButton
                onClick={() => handleOpenWeb(activeWebsite)}
                title='Visit Website'
                sx={{
                  width: '48px !important',
                  height: '48px !important',
                  minWidth: '48px !important',
                  minHeight: '48px !important',
                  padding: '0 !important',
                  border: '2px solid #FF9900',
                  borderRadius: '6px',
                  color: '#00008B',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}>
                <LanguageIcon sx={{ fontSize: '32px !important' }} />
              </IconButton>
            </Box>
          )}

          {descriptionContent && (
            <Box
              sx={{
                mt: activeWebsite && activeWebsite.trim() !== '' ? 3 : 0,
                typography: 'body1',
                color: 'text.secondary',
                lineHeight: 1.2,
                minHeight: '20px',
                whiteSpace: 'pre-wrap', // Preserve natural line breaks
                wordBreak: 'break-word',
                textTransform: 'none !important', // AMENDED: Prevent global theme uppercase leakage
                '& p': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.2 }, // AMENDED: Sync with RTE margins
                '& p:last-of-type': { marginBottom: 0 },
                '& img': { maxWidth: '100%', height: 'auto' },
                '& ul, & ol': {
                  paddingLeft: '1.5rem',
                  marginTop: 0,
                  marginBottom: 0,
                },
                '& li': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.1 },
                '& li > p': { margin: '0 !important' },
                '& a': { color: '#1976d2', textDecoration: 'none' },
              }}
              dangerouslySetInnerHTML={{ __html: descriptionContent }}
            />
          )}

          {showDescPanel && (
            <Button
              className='see-more-btn'
              onClick={(e) => {
                e.stopPropagation();
                setIsDescriptionExpanded(!isDescriptionExpanded);
              }}
              variant='contained'
              size='small'
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 45,
                zIndex: 10,
                textTransform: 'none',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
              }}>
              {isDescriptionExpanded ? 'See less ...' : 'See more ...'}
            </Button>
          )}
        </Box>

        <Box
          className='resize-handle'
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerUp}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '30px',
            height: '30px',
            cursor: 'nwse-resize',
            zIndex: 1000,
            pointerEvents: 'auto',
            touchAction: 'none',
            background:
              'linear-gradient(135deg, transparent 50%, rgba(128,128,128,0.8) 50%)',
            '&:hover': {
              background:
                'linear-gradient(135deg, transparent 50%, rgba(128,128,128,1) 50%)',
            },
          }}
          title='Drag to resize'
        />
      </>
    );
  };

  return (
    <>
      <Portal>
        <Box
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          sx={{
            position: 'fixed',
            top: containerPos.y,
            left: containerPos.x,
            width: `${containerSize}px`,
            height: `${containerSize}px`,
            zIndex: 1600,
            backgroundColor: 'transparent',
            pointerEvents: 'none',
            touchAction: 'none',
            transition:
              isDragging || resizeInfo.current.isResizing
                ? 'none'
                : 'all 0.3s ease-in-out',
          }}>
          {renderContent()}
        </Box>
      </Portal>

      {/* NEW FOOTER */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 500, // Above the map, below the floating media modal
          pointerEvents: 'auto',
        }}>
        <Typography
          variant='caption'
          sx={{
            color: '#333',
            fontWeight: 600,
            textShadow: '0px 1px 3px rgba(255,255,255,0.8)',
          }}>
          <button
            type='button'
            onClick={(e) => {
              e.preventDefault();
              setIsContactOpen(true);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#0055ff',
              cursor: 'pointer',
              font: 'inherit',
            }}>
            info@loktr.net
          </button>
        </Typography>
        <Typography
          variant='caption'
          sx={{
            color: '#333',
            fontWeight: 600,
            textShadow: '0px 1px 3px rgba(255,255,255,0.8)',
          }}>
          Version : {packageInfo.version}
        </Typography>
        <Typography
          variant='caption'
          sx={{
            color: '#333',
            fontWeight: 600,
            textShadow: '0px 1px 3px rgba(255,255,255,0.8)',
          }}>
          (c) RTA Technology Pty Ltd. All rights reserved.
        </Typography>
      </Box>

      <ConfirmDialog
        open={showUrlError}
        onClose={() => setShowUrlError(false)}
        message='The Web Site text does not appear to be a valid URL format.'
        severity='warning'
        autoClose={3000}
        disableRestoreFocus={true}
        cancelText={null}
        sx={{ zIndex: 90005 }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />

      <ContactDialog open={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
}
